/**
 * AgentMovementPanel.tsx
 *
 * Operator-facing controls for the server-side AI-agent movement
 * loop (Phase 2 / slice 1c).
 *
 * Surface:
 *   - Predefined waypoints editor — add via the same Mapbox
 *     typeahead the AgentTeleportHUD uses (so the suggestion
 *     coverage and country-scoping are identical), reorder by
 *     ↑ / ↓, remove individual rows.
 *   - Mode toggle — predefined / random / both. "Random" pulls
 *     POIs from GeoLink's geocoding cache for the configured
 *     country (`agentRandomPlaceService`).
 *   - Country selector — defaults to the agent's residency if
 *     known, falls back to the first predefined waypoint's
 *     country, otherwise required for `random` / `both`.
 *   - Cadence — 5 s minimum (matches GeoLink's per-agent write
 *     guard with headroom), 30 min maximum.
 *   - Start / Pause / Resume / Stop — driven by a session token
 *     minted via `agentMovementService.ensureMovementSession`.
 *   - Live status panel — current cursor, last waypoint, last
 *     nearby snapshot (other agents + geofence hits), tick count.
 *
 * Smart-account agents (C…) work end-to-end since GeoLink
 * migration 028 (2026-05-09) widened `agent_pk` to
 * `^[GC][A-Z2-7]{55}$` across all `/api/agents/*` endpoints.
 * The position write, /nearby probe, and the public live feed
 * all carry an `address_kind: 'classic' | 'smart_account'`
 * marker; we surface it as a small footnote chip in the
 * nearby list so the operator can tell who's a passkey wallet
 * vs a classic Stellar account at a glance.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ensureMovementSession,
  clearMovementSession,
  getMovementStatus,
  getMovementWaypoints,
  setMovementWaypoints,
  startMovement,
  pauseMovement,
  resumeMovement,
  stopMovement,
  type MovementMode,
  type MovementRandomDiagnostics,
  type MovementStatusResponse,
  type MovementWaypoint,
} from '../services/agentMovementService';
import {
  suggestPlaces,
  type TeleportPlaceSuggestion,
} from '../services/teleportPlaceSearch';
import type { SigningWallet, PasskeySigner } from '../services/profileService';
import { humanizeUnknownError } from '../utils/sorobanContractErrors';
import {
  describeMovementError,
  type MovementErrorText,
} from '../utils/movementErrorText';

interface AgentMovementPanelProps {
  agentAddress:    string;
  operatorWallet:  string;
  /** Agent residency (ISO-3166 alpha-2). Used as the default country
   *  for the random / both modes when the operator hasn't picked one. */
  residencyCountry?: string | null;
  stellarWallet:   SigningWallet | null;
  passkey:         PasskeySigner | null;
}

const STELLAR_C = /^C[A-Z2-7]{55}$/;
const POLL_INTERVAL_MS = 4_000;
const ISO2 = /^[A-Z]{2}$/;

const describeError = (e: unknown): MovementErrorText =>
  describeMovementError(e, humanizeUnknownError);

const AgentMovementPanel: React.FC<AgentMovementPanelProps> = ({
  agentAddress,
  operatorWallet,
  residencyCountry,
  stellarWallet,
  passkey,
}) => {
  const [status,    setStatus]    = useState<MovementStatusResponse | null>(null);
  const [waypoints, setWaypoints] = useState<MovementWaypoint[]>([]);
  const [loading,   setLoading]   = useState(true);
  const [err,       setErr]       = useState<MovementErrorText | null>(null);
  const [busy,      setBusy]      = useState<null | 'start' | 'pause' | 'resume' | 'stop' | 'save'>(null);

  const [mode,       setMode]       = useState<MovementMode>('predefined');
  const [country,    setCountry]    = useState<string>(() => (residencyCountry || '').toUpperCase());
  const [intervalS,  setIntervalS]  = useState<number>(30);

  const [search,           setSearch]           = useState('');
  const [suggestions,      setSuggestions]      = useState<TeleportPlaceSuggestion[]>([]);
  const [searchFocused,    setSearchFocused]    = useState(false);
  const searchAbortRef = useRef<AbortController | null>(null);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isCAgent = STELLAR_C.test(agentAddress);

  // Initial load + waypoint fetch.
  const refreshAll = useCallback(async () => {
    setErr(null);
    try {
      const [s, w] = await Promise.all([
        getMovementStatus(agentAddress).catch(() => null),
        getMovementWaypoints(agentAddress).catch(() => []),
      ]);
      if (s) {
        setStatus(s);
        setMode(s.mode);
        if (s.country) setCountry(s.country);
        if (s.intervalMs) setIntervalS(Math.round(s.intervalMs / 1000));
      }
      setWaypoints(w);
    } catch (e: any) {
      setErr({ text: e?.message || 'Failed to read movement state', auth: false });
    } finally {
      setLoading(false);
    }
  }, [agentAddress]);

  useEffect(() => { void refreshAll(); }, [refreshAll]);

  // Status polling — only when mounted; pauses when tab is hidden
  // so a backgrounded profile doesn't hammer GeoLink.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      try {
        const s = await getMovementStatus(agentAddress);
        if (!cancelled) setStatus(s);
      } catch { /* ignore poll errors */ }
    };
    const id = setInterval(tick, POLL_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [agentAddress]);

  // Mapbox typeahead for the predefined editor — same plumbing as
  // the teleport HUD's place search. Country hint comes from the
  // currently configured loop country so "Springfield" disambiguates
  // correctly.
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    if (searchAbortRef.current)    searchAbortRef.current.abort();
    const q = search.trim();
    if (q.length < 2) { setSuggestions([]); return; }
    searchDebounceRef.current = setTimeout(async () => {
      const ac = new AbortController();
      searchAbortRef.current = ac;
      const list = await suggestPlaces(q, {
        country: country || residencyCountry || null,
        signal:  ac.signal,
      });
      if (searchAbortRef.current === ac) setSuggestions(list);
    }, 200);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [search, country, residencyCountry]);

  // ── Mutations ────────────────────────────────────────────────────────────

  const ensureSession = useCallback(async () => {
    return ensureMovementSession({
      operator: operatorWallet,
      agent:    agentAddress,
      stellarWallet,
      passkey,
    });
  }, [agentAddress, operatorWallet, stellarWallet, passkey]);

  // Bag of signers we hand to every write helper so the
  // authedFetch 401-retry can transparently re-mint a fresh
  // session when the cached token has gone stale (backend secret
  // rotated, registry lookup recovered, etc.).
  const writeSigners = useMemo(
    () => ({ stellarWallet, passkey }),
    [stellarWallet, passkey],
  );

  const handleAddSuggestion = useCallback((s: TeleportPlaceSuggestion) => {
    setWaypoints((prev) => [
      ...prev,
      {
        name:    s.placeName,
        lat:     s.lat,
        lng:     s.lng,
        country: s.country || country || null,
        source:  'manual',
      },
    ]);
    setSearch('');
    setSuggestions([]);
  }, [country]);

  const handleRemoveWaypoint = useCallback((idx: number) => {
    setWaypoints((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const handleMoveWaypoint = useCallback((idx: number, delta: -1 | 1) => {
    setWaypoints((prev) => {
      const j = idx + delta;
      if (j < 0 || j >= prev.length) return prev;
      const next = prev.slice();
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  }, []);

  const handleSaveWaypoints = useCallback(async () => {
    setErr(null); setBusy('save');
    try {
      await ensureSession();
      const s = await setMovementWaypoints(operatorWallet, agentAddress, waypoints, writeSigners);
      setStatus(s);
    } catch (e: unknown) {
      setErr(describeError(e));
    } finally { setBusy(null); }
  }, [agentAddress, ensureSession, operatorWallet, waypoints, writeSigners]);

  const handleStart = useCallback(async () => {
    setErr(null); setBusy('start');
    try {
      await ensureSession();
      // Nothing to sync when the list is empty, and writing an empty
      // list here would clear whatever the server holds. Clearing is
      // left to "Save list", where it is what the operator asked for.
      if (waypoints.length > 0) {
        await setMovementWaypoints(operatorWallet, agentAddress, waypoints, writeSigners);
      }
      const s = await startMovement(operatorWallet, agentAddress, {
        mode,
        country: country || undefined,
        intervalMs: Math.max(5_000, Math.floor(intervalS * 1000)),
      }, writeSigners);
      setStatus(s);
    } catch (e: unknown) {
      setErr(describeError(e));
    } finally { setBusy(null); }
  }, [agentAddress, country, ensureSession, intervalS, mode, operatorWallet, waypoints, writeSigners]);

  const handlePause = useCallback(async () => {
    setErr(null); setBusy('pause');
    try {
      await ensureSession();
      const s = await pauseMovement(operatorWallet, agentAddress, writeSigners);
      setStatus(s);
    } catch (e: unknown) {
      setErr(describeError(e));
    } finally { setBusy(null); }
  }, [agentAddress, ensureSession, operatorWallet, writeSigners]);

  const handleResume = useCallback(async () => {
    setErr(null); setBusy('resume');
    try {
      await ensureSession();
      const s = await resumeMovement(operatorWallet, agentAddress, writeSigners);
      setStatus(s);
    } catch (e: unknown) {
      setErr(describeError(e));
    } finally { setBusy(null); }
  }, [agentAddress, ensureSession, operatorWallet, writeSigners]);

  const handleStop = useCallback(async () => {
    setErr(null); setBusy('stop');
    try {
      await ensureSession();
      const s = await stopMovement(operatorWallet, agentAddress, writeSigners);
      setStatus(s);
    } catch (e: unknown) {
      setErr(describeError(e));
    } finally { setBusy(null); }
  }, [agentAddress, ensureSession, operatorWallet, writeSigners]);

  const handleResetSession = useCallback(() => {
    clearMovementSession(operatorWallet, agentAddress);
    setErr(null);
  }, [agentAddress, operatorWallet]);

  // ── Derived ──────────────────────────────────────────────────────────────

  const running = status?.status === 'running';
  const paused  = status?.status === 'paused';

  const nextCursor = useMemo(() => {
    if (!status || waypoints.length === 0) return null;
    return waypoints[(status.cursor % waypoints.length)] || null;
  }, [status, waypoints]);

  // A half-typed code ("A") is truthy but the server demands a
  // complete ISO-2, so testing for emptiness alone let Start fire a
  // request that could only ever come back 400.
  const countryValid = ISO2.test(country);
  const needsCountry = mode === 'random' || mode === 'both';

  const startDisabled =
    !!busy
    || (mode === 'predefined' && waypoints.length === 0)
    || (needsCountry && !countryValid);

  if (loading) {
    return (
      <section style={cardStyle}>
        <h4 style={titleStyle}>Movement loop</h4>
        <div style={{ color: '#6b7280', fontSize: 13 }}>Loading movement state…</div>
      </section>
    );
  }

  return (
    <section style={cardStyle}>
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <h4 style={titleStyle}>Movement loop</h4>
        <StatusPill status={status?.status || 'stopped'} />
      </header>

      {isCAgent && (
        <div style={smartAccountNoteStyle}>
          Smart-account agent (<code>C…</code>). Positions and nearby reads
          go through the same GeoLink surface as classic agents since
          migration <code>028_widen_agent_pk_to_smart_accounts</code>.
        </div>
      )}

      {err && (
        <div style={errorBoxStyle}>
          <strong>Couldn't update movement.</strong> {err.text}
          {err.auth && (
            <button onClick={handleResetSession} style={inlineLinkStyle}>
              Reset signed session
            </button>
          )}
        </div>
      )}

      {/* ── Mode + cadence + country ───────────────────────────── */}
      <div style={controlsRowStyle}>
        <label style={labelStyle}>
          <span style={labelTextStyle}>Mode</span>
          <select
            value={mode}
            onChange={(e) => setMode(e.target.value as MovementMode)}
            disabled={running || paused}
            style={inputStyle}
          >
            <option value="predefined">Predefined waypoints (cycle)</option>
            <option value="random">Random POIs in country</option>
            <option value="both">Both — alternate predefined / random</option>
          </select>
        </label>
        <label style={labelStyle}>
          <span style={labelTextStyle}>Country (ISO-2)</span>
          <input
            type="text"
            value={country}
            onChange={(e) => setCountry(
              e.target.value.replace(/[^A-Za-z]/g, '').toUpperCase().slice(0, 2),
            )}
            placeholder="US"
            disabled={running || paused}
            style={{ ...inputStyle, fontFamily: 'monospace' }}
            maxLength={2}
          />
          {needsCountry && !countryValid && (
            <span style={fieldHintStyle}>
              {country ? 'Two letters needed — e.g. AE.' : 'Required for this mode.'}
            </span>
          )}
        </label>
        <label style={labelStyle}>
          <span style={labelTextStyle}>Interval (s) — {intervalS}</span>
          <input
            type="range"
            min={5}
            max={600}
            step={1}
            value={intervalS}
            onChange={(e) => setIntervalS(Number(e.target.value))}
            disabled={running || paused}
            style={{ width: '100%' }}
          />
        </label>
      </div>

      {/* ── Predefined waypoints editor ────────────────────────── */}
      <div style={sectionStyle}>
        <div style={subTitleRowStyle}>
          <h5 style={subTitleStyle}>Predefined waypoints ({waypoints.length})</h5>
          <button
            type="button"
            onClick={() => void handleSaveWaypoints()}
            disabled={!!busy}
            style={busy === 'save' ? smallBtnDisabledStyle : smallBtnStyle}
          >
            {busy === 'save' ? 'Saving…' : 'Save list'}
          </button>
        </div>

        {waypoints.length === 0 ? (
          <div style={mutedStyle}>
            No waypoints yet. Search a place below, or enable "Random POIs" mode and just press Start.
          </div>
        ) : (
          <ol style={waypointListStyle}>
            {waypoints.map((w, idx) => (
              <li key={`${w.name || 'wp'}-${idx}`} style={waypointRowStyle}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={waypointTitleStyle} title={w.name || ''}>
                    {idx === (status?.cursor || 0) && running && (
                      <span style={cursorPillStyle}>now</span>
                    )}
                    {w.name || `${w.lat.toFixed(4)}, ${w.lng.toFixed(4)}`}
                  </div>
                  <div style={waypointMetaStyle}>
                    {w.lat.toFixed(5)}, {w.lng.toFixed(5)}
                    {w.country ? ` · ${w.country}` : ''}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button
                    type="button"
                    aria-label="Move up"
                    onClick={() => handleMoveWaypoint(idx, -1)}
                    disabled={idx === 0 || running || paused}
                    style={iconBtnStyle}
                  >↑</button>
                  <button
                    type="button"
                    aria-label="Move down"
                    onClick={() => handleMoveWaypoint(idx, 1)}
                    disabled={idx === waypoints.length - 1 || running || paused}
                    style={iconBtnStyle}
                  >↓</button>
                  <button
                    type="button"
                    aria-label="Remove waypoint"
                    onClick={() => handleRemoveWaypoint(idx)}
                    disabled={running || paused}
                    style={iconBtnStyle}
                  >✕</button>
                </div>
              </li>
            ))}
          </ol>
        )}

        {/* Mapbox typeahead for adding new waypoints */}
        <div style={{ marginTop: 8, position: 'relative' }}>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setTimeout(() => setSearchFocused(false), 150)}
            placeholder={
              country
                ? `Add a waypoint in ${country}…`
                : 'Add a waypoint (any country — set the country above for cache scoping)'
            }
            disabled={running || paused}
            style={{ ...inputStyle, width: '100%' }}
            spellCheck={false}
          />
          {searchFocused && suggestions.length > 0 && (
            <ul role="listbox" style={suggestionsListStyle}>
              {suggestions.map((s) => (
                <li key={s.id}>
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleAddSuggestion(s)}
                    style={suggestionRowStyle}
                  >
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{s.placeName}</div>
                    <div style={{ fontSize: 11, color: '#6b7280' }}>
                      {s.lat.toFixed(4)}, {s.lng.toFixed(4)}{s.country ? ` · ${s.country}` : ''}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* ── Control buttons ────────────────────────────────────── */}
      <div style={btnRowStyle}>
        {!running && !paused && (
          <button
            type="button"
            onClick={() => void handleStart()}
            disabled={startDisabled}
            style={startDisabled ? primaryBtnDisabledStyle : primaryBtnStyle}
            title={
              startDisabled && mode === 'predefined' && waypoints.length === 0
                ? 'Add at least one waypoint first.'
                : startDisabled && needsCountry && !countryValid
                ? 'A complete two-letter country code is required for random / both modes.'
                : 'Start the movement loop.'
            }
          >
            {busy === 'start' ? 'Starting…' : '▶ Start'}
          </button>
        )}
        {running && (
          <button type="button" onClick={() => void handlePause()} disabled={!!busy} style={secondaryBtnStyle}>
            {busy === 'pause' ? 'Pausing…' : '⏸ Pause'}
          </button>
        )}
        {paused && (
          <button type="button" onClick={() => void handleResume()} disabled={!!busy} style={primaryBtnStyle}>
            {busy === 'resume' ? 'Resuming…' : '▶ Resume'}
          </button>
        )}
        {(running || paused) && (
          <button type="button" onClick={() => void handleStop()} disabled={!!busy} style={dangerBtnStyle}>
            {busy === 'stop' ? 'Stopping…' : '■ Stop'}
          </button>
        )}
      </div>

      {/* ── Live status ────────────────────────────────────────── */}
      {status?.exists && (status.lastWaypoint || status.lastNearby || nextCursor) && (
        <div style={statusGridStyle}>
          {status.lastWaypoint && (
            <div style={statusBoxStyle}>
              <div style={statusLabel}>Last hop · tick {status.ticks}</div>
              <div style={statusValue}>
                {status.lastWaypoint.name || `${status.lastWaypoint.lat.toFixed(4)}, ${status.lastWaypoint.lng.toFixed(4)}`}
                {status.lastWaypoint.repeated && (
                  <span style={repeatPillStyle} title="Cache shallow — re-using a recent POI">
                    ↻ repeat
                  </span>
                )}
                {status.lastWaypoint.nearby_fallback && (
                  <span style={nearbyPillStyle} title="Slug-based search came up dry — picked a cached entry near the agent's last position">
                    ⊕ nearby
                  </span>
                )}
              </div>
              <div style={statusMeta}>
                {status.lastWaypoint.source}
                {status.lastWaypoint.position_write_ok && ' · written ✓'}
                {status.lastWaypoint.position_write_skipped &&
                  ` · skipped (${status.lastWaypoint.position_write_reason})`}
                {!status.lastWaypoint.position_write_ok && !status.lastWaypoint.position_write_skipped &&
                  ` · ${status.lastWaypoint.position_write_reason || 'failed'}`}
              </div>
            </div>
          )}
          {nextCursor && (running || paused) && (
            <div style={statusBoxStyle}>
              <div style={statusLabel}>Next hop</div>
              <div style={statusValue}>
                {nextCursor.name || `${nextCursor.lat.toFixed(4)}, ${nextCursor.lng.toFixed(4)}`}
              </div>
              <div style={statusMeta}>
                cursor {status.cursor + 1}/{waypoints.length}
              </div>
            </div>
          )}
          {status.lastNearby && (
            <div style={{ ...statusBoxStyle, gridColumn: '1 / -1' }}>
              <div style={statusLabel}>Nearby (radius {status.lastNearby.radius_m ?? '?'} m)</div>
              {status.lastNearby.nearby_agents.length === 0
                && status.lastNearby.geofence_hits.length === 0 && (
                  <div style={mutedStyle}>Nothing within range.</div>
                )}
              {status.lastNearby.nearby_agents.length > 0 && (
                <div style={{ marginTop: 6 }}>
                  <div style={subSectionLabelStyle}>Agents</div>
                  <ul style={miniListStyle}>
                    {status.lastNearby.nearby_agents.map((a) => (
                      <li key={a.agent_pk} style={miniListItemStyle}>
                        <code style={miniMonoStyle}>{shortAddr(a.agent_pk)}</code>
                        {a.address_kind && (
                          <span style={a.address_kind === 'smart_account' ? kindPillSmartStyle : kindPillClassicStyle}>
                            {a.address_kind === 'smart_account' ? 'passkey' : 'classic'}
                          </span>
                        )}
                        {a.displayName ? ` · ${a.displayName}` : ''}
                        {a.distance_m != null ? ` · ${Math.round(a.distance_m)} m` : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {status.lastNearby.geofence_hits.length > 0 && (
                <div style={{ marginTop: 6 }}>
                  <div style={subSectionLabelStyle}>Geofence hits</div>
                  <ul style={miniListStyle}>
                    {status.lastNearby.geofence_hits.map((g, i) => (
                      <li key={`${g.id || g.name || i}`} style={miniListItemStyle}>
                        {g.name || g.id || '(unnamed)'}{g.kind ? ` · ${g.kind}` : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {status?.lastError && (
        <div style={inlineWarnStyle}>
          <strong>tick {status.lastError.tick}:</strong>{' '}
          {prettyTickReason(status.lastError.reason)}
          {status.lastError.message ? <div style={{ marginTop: 4, fontSize: 11, opacity: 0.85 }}>{status.lastError.message}</div> : null}
        </div>
      )}

      {status?.randomDiagnostics && (status.randomDiagnostics.slugAttempts > 0 || status.randomDiagnostics.hotSlugs.length > 0) && (
        <RandomDiagnosticsCard diag={status.randomDiagnostics} />
      )}
    </section>
  );
};

export default AgentMovementPanel;

// ── Sub-components ─────────────────────────────────────────────────────────

/**
 * Compact card that shows the random POI picker's diagnostics
 * for the agent's country: how many slug attempts vs how many
 * GeoLink cache hits, plus the current hot-slug shortlist.
 *
 * The point is to make a `cache_cold` warning at tick 86
 * actionable: if the operator sees `0 hits / 86 attempts` they
 * know GeoLink isn't returning anything for that country (route
 * not deployed, response shape changed, etc.); if they see
 * `12 hits / 86 attempts` and a healthy hot list they know
 * cache_cold was just an unlucky exploration sample.
 */
const RandomDiagnosticsCard: React.FC<{ diag: MovementRandomDiagnostics }> = ({ diag }) => {
  const attempts = diag.slugAttempts;
  const hitRate  = attempts > 0 ? (diag.slugHits / attempts) : 0;
  const dryRun   = attempts >= 6 && diag.slugHits === 0;
  const cardStyle: React.CSSProperties = {
    marginTop: 8,
    padding: '8px 10px',
    background: dryRun ? '#fef2f2' : '#f8fafc',
    color: dryRun ? '#991b1b' : '#334155',
    border: dryRun ? '1px solid #fecaca' : '1px solid #e2e8f0',
    borderRadius: 8,
    fontSize: 11,
    lineHeight: 1.5,
  };
  return (
    <div style={cardStyle}>
      <div style={{ fontWeight: 700, marginBottom: 2 }}>
        Random POI cache · {diag.country}
      </div>
      <div>
        slug hits <strong>{diag.slugHits}</strong> / <strong>{attempts}</strong>
        {' '}({Math.round(hitRate * 100)}%)
        {diag.nearbyAttempts > 0 && (
          <>
            {' · '}nearby <strong>{diag.nearbyHits}</strong> / <strong>{diag.nearbyAttempts}</strong>
          </>
        )}
      </div>
      {diag.hotSlugs.length > 0 ? (
        <div style={{ marginTop: 4 }}>
          warm: {diag.hotSlugs.slice(0, 8).map((s) => (
            <span key={s} style={{
              display: 'inline-block',
              marginRight: 4,
              padding: '0 6px',
              background: '#dcfce7',
              color: '#166534',
              border: '1px solid #bbf7d0',
              borderRadius: 6,
              fontSize: 10,
              fontWeight: 600,
            }}>{s}</span>
          ))}
          {diag.hotSlugs.length > 8 ? <span style={{ opacity: 0.6 }}>+{diag.hotSlugs.length - 8}</span> : null}
        </div>
      ) : null}
      {dryRun && (
        <div style={{ marginTop: 4, fontSize: 10 }}>
          GeoLink hasn't returned a single hit for {diag.country} in {attempts} tries.
          The geocoding cache endpoint may not be deployed, the response shape may
          have changed, or this country genuinely has no cached entries. Check the
          backend logs for 404s / parse failures on
          <code> /api/geocoding/cache</code>.
        </div>
      )}
    </div>
  );
};

const StatusPill: React.FC<{ status: 'stopped' | 'running' | 'paused' }> = ({ status }) => {
  const map: Record<string, React.CSSProperties> = {
    stopped: { background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb' },
    running: { background: '#dcfce7', color: '#15803d', border: '1px solid #bbf7d0' },
    paused:  { background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a' },
  };
  return (
    <span style={{
      ...map[status],
      padding: '2px 8px',
      borderRadius: 999,
      fontSize: 11,
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: 0.4,
    }}>
      {status}
    </span>
  );
};

// ── Helpers ────────────────────────────────────────────────────────────────

function shortAddr(a: string): string {
  if (!a || a.length < 12) return a || '';
  return `${a.slice(0, 5)}…${a.slice(-5)}`;
}

/**
 * Map the raw tick error keys returned by `agentMovementService`
 * into short, operator-readable headlines. The `message` field
 * (rendered separately by the caller) carries the longer, more
 * specific hint.
 */
function prettyTickReason(reason: string): string {
  switch (reason) {
    case 'no_place_returned':         return 'No random POI available.';
    case 'cache_cold':                return 'GeoLink cache is cold for this country.';
    case 'geolink_not_configured':    return 'GeoLink isn\'t configured on this server.';
    case 'invalid_country':           return 'Country code is invalid.';
    case 'no_country_set':            return 'No country set — open the editor and pick one.';
    case 'no_predefined_waypoints':   return 'No predefined waypoints — add some, or switch mode to "random".';
    case 'random_place_service_unavailable':
                                      return 'Random place service is unavailable on this server.';
    case 'random_place_error':        return 'Random place lookup failed.';
    default:                          return reason;
  }
}

// ── Styles ─────────────────────────────────────────────────────────────────

const cardStyle: React.CSSProperties = {
  marginTop: 12,
  padding: 14,
  borderRadius: 10,
  border: '1px solid #e5e7eb',
  background: '#fff',
};

const titleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 14,
  fontWeight: 700,
  color: '#111827',
};

const sectionStyle: React.CSSProperties = { marginTop: 12 };

const subTitleRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  marginBottom: 6,
};

const subTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 13,
  fontWeight: 700,
  color: '#374151',
};

const controlsRowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1.6fr) minmax(0, 0.6fr) minmax(0, 1fr)',
  gap: 10,
  marginTop: 8,
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  minWidth: 0,
};

const labelTextStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 11,
  fontWeight: 600,
  color: '#6b7280',
  marginBottom: 4,
  textTransform: 'uppercase',
  letterSpacing: 0.4,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '7px 9px',
  borderRadius: 6,
  border: '1px solid #d1d5db',
  fontSize: 13,
};

const fieldHintStyle: React.CSSProperties = {
  display: 'block',
  marginTop: 4,
  fontSize: 11,
  color: '#b45309',
};

const waypointListStyle: React.CSSProperties = {
  margin: 0,
  padding: 0,
  listStyle: 'none',
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
};

const waypointRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  padding: '6px 8px',
  borderRadius: 6,
  background: '#f9fafb',
  border: '1px solid #f3f4f6',
};

const waypointTitleStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 13,
  fontWeight: 600,
  color: '#111827',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const waypointMetaStyle: React.CSSProperties = {
  fontSize: 11,
  color: '#6b7280',
  fontFamily: 'monospace',
};

const cursorPillStyle: React.CSSProperties = {
  background: '#dcfce7',
  color: '#15803d',
  border: '1px solid #bbf7d0',
  padding: '0 6px',
  borderRadius: 999,
  fontSize: 10,
  fontWeight: 700,
  textTransform: 'uppercase',
};

const iconBtnStyle: React.CSSProperties = {
  width: 28,
  height: 28,
  borderRadius: 6,
  border: '1px solid #e5e7eb',
  background: '#fff',
  fontSize: 13,
  cursor: 'pointer',
  color: '#374151',
  padding: 0,
};

const suggestionsListStyle: React.CSSProperties = {
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  margin: '4px 0 0',
  padding: 0,
  listStyle: 'none',
  background: '#fff',
  border: '1px solid #d1d5db',
  borderRadius: 6,
  boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)',
  zIndex: 5,
  maxHeight: 240,
  overflowY: 'auto',
};

const suggestionRowStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  textAlign: 'left',
  border: 'none',
  background: 'transparent',
  padding: '8px 10px',
  cursor: 'pointer',
  borderBottom: '1px solid #f3f4f6',
};

const btnRowStyle: React.CSSProperties = {
  display: 'flex',
  gap: 8,
  marginTop: 12,
  flexWrap: 'wrap',
};

const primaryBtnStyle: React.CSSProperties = {
  padding: '8px 14px',
  borderRadius: 999,
  border: '1px solid #15803d',
  background: '#16a34a',
  color: '#fff',
  fontSize: 13,
  fontWeight: 700,
  cursor: 'pointer',
};

const primaryBtnDisabledStyle: React.CSSProperties = {
  ...primaryBtnStyle,
  background: '#bbf7d0',
  borderColor: '#bbf7d0',
  color: '#166534',
  cursor: 'not-allowed',
};

const secondaryBtnStyle: React.CSSProperties = {
  padding: '8px 14px',
  borderRadius: 999,
  border: '1px solid #d1d5db',
  background: '#fff',
  color: '#111827',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};

const dangerBtnStyle: React.CSSProperties = {
  padding: '8px 14px',
  borderRadius: 999,
  border: '1px solid #b91c1c',
  background: '#fff',
  color: '#b91c1c',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};

const smallBtnStyle: React.CSSProperties = {
  padding: '4px 10px',
  borderRadius: 999,
  border: '1px solid #d1d5db',
  background: '#fff',
  fontSize: 12,
  cursor: 'pointer',
  color: '#111827',
};

const smallBtnDisabledStyle: React.CSSProperties = {
  ...smallBtnStyle,
  opacity: 0.6,
  cursor: 'not-allowed',
};

const errorBoxStyle: React.CSSProperties = {
  padding: '10px 12px',
  background: '#fef2f2',
  color: '#991b1b',
  borderRadius: 8,
  fontSize: 13,
  marginBottom: 8,
  marginTop: 8,
};

// One-line footnote (down-graded from the previous yellow banner)
// for smart-account agents now that GeoLink stores their positions
// natively. Sits inline below the header.
const smartAccountNoteStyle: React.CSSProperties = {
  padding: '6px 10px',
  background: '#f1f5f9',
  color: '#334155',
  borderRadius: 6,
  fontSize: 11,
  border: '1px solid #e2e8f0',
  marginBottom: 8,
  lineHeight: 1.45,
};

const kindPillBaseStyle: React.CSSProperties = {
  display: 'inline-block',
  marginLeft: 6,
  padding: '0 5px',
  borderRadius: 6,
  fontSize: 9,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  verticalAlign: 'middle',
  border: '1px solid transparent',
};

const kindPillSmartStyle: React.CSSProperties = {
  ...kindPillBaseStyle,
  background: '#ede9fe',
  color: '#5b21b6',
  borderColor: '#ddd6fe',
};

const kindPillClassicStyle: React.CSSProperties = {
  ...kindPillBaseStyle,
  background: '#e0f2fe',
  color: '#075985',
  borderColor: '#bae6fd',
};

// Inline "↻ repeat" badge for the Last hop card when the random
// picker had to re-use a recent POI because the cache is shallow.
// Visual is intentionally calmer than the warn banner — repeating
// is fine, it just signals that warming the cache would help.
const repeatPillStyle: React.CSSProperties = {
  ...kindPillBaseStyle,
  background: '#fef3c7',
  color: '#92400e',
  borderColor: '#fde68a',
  marginLeft: 8,
};

// "⊕ nearby" badge for the Last hop card when the slug-based
// random picker missed but the lat/lng GeoLink cache lookup
// found a cached entry near the agent's previous position.
// Calmer green so it doesn't read as an error — the loop is
// healthy, it's just routing around a thin slug index.
const nearbyPillStyle: React.CSSProperties = {
  ...kindPillBaseStyle,
  background: '#dcfce7',
  color: '#166534',
  borderColor: '#bbf7d0',
  marginLeft: 8,
};

const inlineLinkStyle: React.CSSProperties = {
  marginLeft: 8,
  fontSize: 12,
  background: 'transparent',
  border: 'none',
  color: '#1d4ed8',
  cursor: 'pointer',
  textDecoration: 'underline',
};

const inlineWarnStyle: React.CSSProperties = {
  marginTop: 8,
  fontSize: 12,
  color: '#b45309',
};

const mutedStyle: React.CSSProperties = {
  color: '#6b7280',
  fontSize: 12,
};

const statusGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
  gap: 8,
  marginTop: 12,
};

const statusBoxStyle: React.CSSProperties = {
  padding: 10,
  borderRadius: 8,
  background: '#f9fafb',
  border: '1px solid #f3f4f6',
};

const statusLabel: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: '#6b7280',
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  marginBottom: 4,
};

const statusValue: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  color: '#111827',
};

const statusMeta: React.CSSProperties = {
  fontSize: 11,
  color: '#6b7280',
  marginTop: 2,
};

const subSectionLabelStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  color: '#374151',
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  marginBottom: 4,
};

const miniListStyle: React.CSSProperties = {
  margin: 0,
  padding: 0,
  listStyle: 'none',
};

const miniListItemStyle: React.CSSProperties = {
  fontSize: 12,
  color: '#374151',
  padding: '2px 0',
};

const miniMonoStyle: React.CSSProperties = {
  fontFamily: 'monospace',
  fontSize: 11,
  color: '#374151',
};
